import React, {useMemo, useRef} from 'react';
import {makeStyles, Theme, createStyles} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import moment from 'moment';
import {
  APP_PRIMARY_COLOR,
  BORDER_PRIMARY_COLOR,
  DEFAULT_BACKGROUND_COLOR,
  DEFAULT_BLACK_COLOR,
  DEFAULT_GREY_COLOR,
  DEFAULT_WHITE_COLOR,
  LIGHT_BLACK_COLOR
} from 'src/style/variable';

export interface ITimelineSlotItem {
  start_time: string;
  end_time: string;
  duration: number;
}

export interface IBusySlotItem {
  start_time: string;
  end_time: string;
  duration: number;
  reason?: string;
}

export interface IDayWindow {
  start_time: string;
  end_time: string;
}

type SegmentKind = 'available' | 'busy' | 'unavailable';

interface ITimelineSegment {
  kind: SegmentKind;
  startMinutes: number;
  endMinutes: number;
  startLabel: string;
  endLabel: string;
  slot?: ITimelineSlotItem;
}

const TIME_FORMAT = 'h:mm A';
const PIXELS_PER_MINUTE = 1.35;
const DEFAULT_WINDOW_START = 8 * 60;
const DEFAULT_WINDOW_END = 17 * 60;
const NOON_SPLIT = 13 * 60;
// Keeps first/last hour labels (translateY -50%) inside the scroll area
const TRACK_EDGE_PAD_PX = 12;

const parseTimeToMinutes = (time: string): number => {
  const parsed = moment(time, [TIME_FORMAT, 'HH:mm:ss', 'HH:mm']);
  if (!parsed.isValid()) {
    return 0;
  }
  return parsed.hours() * 60 + parsed.minutes();
};

const minutesToLabel = (minutes: number): string =>
  moment().startOf('day').add(minutes, 'minutes').format(TIME_FORMAT);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      width: '100%',
      flex: 1,
      minHeight: '12rem',
      maxHeight: '100%'
    },
    legend: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.75rem 1rem',
      fontSize: '0.95rem',
      color: LIGHT_BLACK_COLOR
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.4rem'
    },
    swatch: {
      width: '0.95rem',
      height: '0.95rem',
      borderRadius: 2,
      border: `1px solid ${BORDER_PRIMARY_COLOR}`
    },
    availableSwatch: {
      backgroundColor: DEFAULT_WHITE_COLOR,
      borderColor: APP_PRIMARY_COLOR
    },
    busySwatch: {
      backgroundColor: '#dbe3f0'
    },
    unavailableSwatch: {
      backgroundImage:
        'repeating-linear-gradient(135deg, #f0f2f5 0, #f0f2f5 4px, #e4e7ec 4px, #e4e7ec 8px)'
    },
    scrollArea: {
      position: 'relative',
      overflowY: 'auto',
      overflowX: 'hidden',
      border: `1px solid ${BORDER_PRIMARY_COLOR}`,
      borderRadius: 4,
      backgroundColor: DEFAULT_BACKGROUND_COLOR,
      flex: 1,
      minHeight: '10rem',
      minWidth: 0,
      width: '100%',
      maxWidth: '100%'
    },
    track: {
      position: 'relative',
      marginLeft: '4.75rem',
      maxWidth: '100%'
    },
    hourLabel: {
      position: 'absolute',
      left: '-4.55rem',
      width: '4.25rem',
      textAlign: 'right',
      fontSize: '0.875rem',
      color: DEFAULT_GREY_COLOR,
      transform: 'translateY(-50%)',
      pointerEvents: 'none'
    },
    hourLine: {
      position: 'absolute',
      left: 0,
      right: 0,
      borderTop: `1px dashed ${BORDER_PRIMARY_COLOR}`,
      pointerEvents: 'none'
    },
    noonMarker: {
      position: 'absolute',
      left: 0,
      right: 0,
      borderTop: `2px solid ${APP_PRIMARY_COLOR}`,
      opacity: 0.35,
      pointerEvents: 'none',
      zIndex: 1
    },
    segment: {
      position: 'absolute',
      left: '0.5rem',
      right: '0.5rem',
      borderRadius: 4,
      padding: '0.2rem 0.6rem',
      fontSize: '1rem',
      display: 'flex',
      alignItems: 'center',
      boxSizing: 'border-box',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis'
    },
    available: {
      backgroundColor: DEFAULT_WHITE_COLOR,
      border: `1px solid ${APP_PRIMARY_COLOR}`,
      color: DEFAULT_BLACK_COLOR,
      cursor: 'pointer',
      zIndex: 2,
      fontFamily: 'inherit',
      textAlign: 'left',
      '&:hover': {
        backgroundColor: 'rgba(40, 132, 255, 0.08)'
      }
    },
    availableSelected: {
      backgroundColor: APP_PRIMARY_COLOR,
      color: DEFAULT_WHITE_COLOR,
      borderColor: APP_PRIMARY_COLOR
    },
    busy: {
      backgroundColor: '#dbe3f0',
      border: `1px solid ${BORDER_PRIMARY_COLOR}`,
      color: LIGHT_BLACK_COLOR,
      zIndex: 2,
      cursor: 'default'
    },
    unavailable: {
      backgroundImage:
        'repeating-linear-gradient(135deg, #f0f2f5 0, #f0f2f5 4px, #e4e7ec 4px, #e4e7ec 8px)',
      border: `1px solid transparent`,
      color: DEFAULT_GREY_COLOR,
      zIndex: 0,
      cursor: 'default'
    },
    empty: {
      padding: '1rem',
      color: LIGHT_BLACK_COLOR,
      [theme.breakpoints.up('md')]: {
        padding: '1.5rem'
      }
    }
  })
);

const buildSegments = ({
  availableSlots,
  busySlots,
  windowStart,
  windowEnd
}: {
  availableSlots: ITimelineSlotItem[];
  busySlots: IBusySlotItem[];
  windowStart: number;
  windowEnd: number;
}): ITimelineSegment[] => {
  const occupied: Array<{
    kind: 'available' | 'busy';
    startMinutes: number;
    endMinutes: number;
    startLabel: string;
    endLabel: string;
    slot?: ITimelineSlotItem;
  }> = [
    ...availableSlots.map(slot => ({
      kind: 'available' as const,
      startMinutes: parseTimeToMinutes(slot.start_time),
      endMinutes: parseTimeToMinutes(slot.end_time),
      startLabel: slot.start_time,
      endLabel: slot.end_time,
      slot
    })),
    ...busySlots.map(slot => ({
      kind: 'busy' as const,
      startMinutes: parseTimeToMinutes(slot.start_time),
      endMinutes: parseTimeToMinutes(slot.end_time),
      startLabel: slot.start_time,
      endLabel: slot.end_time
    }))
  ]
    .filter(
      item =>
        item.endMinutes > windowStart &&
        item.startMinutes < windowEnd &&
        item.endMinutes > item.startMinutes
    )
    .map(item => ({
      ...item,
      startMinutes: Math.max(item.startMinutes, windowStart),
      endMinutes: Math.min(item.endMinutes, windowEnd)
    }))
    .sort((a, b) => a.startMinutes - b.startMinutes);

  const segments: ITimelineSegment[] = [];
  let cursor = windowStart;

  occupied.forEach(item => {
    if (item.startMinutes > cursor) {
      segments.push({
        kind: 'unavailable',
        startMinutes: cursor,
        endMinutes: item.startMinutes,
        startLabel: minutesToLabel(cursor),
        endLabel: minutesToLabel(item.startMinutes)
      });
    }
    segments.push(item);
    cursor = Math.max(cursor, item.endMinutes);
  });

  if (cursor < windowEnd) {
    segments.push({
      kind: 'unavailable',
      startMinutes: cursor,
      endMinutes: windowEnd,
      startLabel: minutesToLabel(cursor),
      endLabel: minutesToLabel(windowEnd)
    });
  }

  return segments;
};

const DaySlotTimeline = ({
  availableSlots,
  busySlots,
  dayWindow,
  selectedSlot,
  onSelectSlot
}: {
  availableSlots: ITimelineSlotItem[];
  busySlots: IBusySlotItem[];
  dayWindow: IDayWindow | null;
  selectedSlot: ITimelineSlotItem | null;
  onSelectSlot: (slot: ITimelineSlotItem) => void;
}): JSX.Element => {
  const classes = useStyles();
  const {t} = useTranslation();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {windowStart, windowEnd} = useMemo(() => {
    if (dayWindow?.start_time && dayWindow?.end_time) {
      return {
        windowStart: parseTimeToMinutes(dayWindow.start_time),
        windowEnd: parseTimeToMinutes(dayWindow.end_time)
      };
    }

    const allTimes = [
      ...availableSlots.flatMap(s => [
        parseTimeToMinutes(s.start_time),
        parseTimeToMinutes(s.end_time)
      ]),
      ...busySlots.flatMap(s => [
        parseTimeToMinutes(s.start_time),
        parseTimeToMinutes(s.end_time)
      ])
    ];

    if (allTimes.length) {
      return {
        windowStart: Math.min(...allTimes),
        windowEnd: Math.max(...allTimes)
      };
    }

    return {
      windowStart: DEFAULT_WINDOW_START,
      windowEnd: DEFAULT_WINDOW_END
    };
  }, [availableSlots, busySlots, dayWindow]);

  const segments = useMemo(
    () =>
      buildSegments({
        availableSlots,
        busySlots,
        windowStart,
        windowEnd
      }),
    [availableSlots, busySlots, windowStart, windowEnd]
  );

  const trackHeight = Math.max(
    (windowEnd - windowStart) * PIXELS_PER_MINUTE + TRACK_EDGE_PAD_PX * 2,
    120
  );

  const hourMarks = useMemo(() => {
    const marks: number[] = [];
    // Include the window start hour so 6:00 AM is labeled when day starts at 6:00
    const firstHour = Math.floor(windowStart / 60) * 60;
    for (let minute = firstHour; minute <= windowEnd; minute += 60) {
      if (minute >= windowStart) {
        marks.push(minute);
      }
    }
    return marks;
  }, [windowStart, windowEnd]);

  if (!segments.length) {
    return (
      <div className={classes.empty}>
        {t(
          'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.NO_APPOINTMENT_SLOTS_AVAILABLE'
        )}
      </div>
    );
  }

  const toTrackTop = (minutes: number): number =>
    (minutes - windowStart) * PIXELS_PER_MINUTE + TRACK_EDGE_PAD_PX;

  const noonOffset =
    NOON_SPLIT >= windowStart && NOON_SPLIT <= windowEnd
      ? toTrackTop(NOON_SPLIT)
      : null;

  return (
    <div className={classes.root}>
      <div className={classes.legend}>
        <div className={classes.legendItem}>
          <span className={`${classes.swatch} ${classes.availableSwatch}`} />
          {t(
            'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.LEGEND_AVAILABLE'
          )}
        </div>
        <div className={classes.legendItem}>
          <span className={`${classes.swatch} ${classes.busySwatch}`} />
          {t(
            'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.LEGEND_BUSY'
          )}
        </div>
        <div className={classes.legendItem}>
          <span className={`${classes.swatch} ${classes.unavailableSwatch}`} />
          {t(
            'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.LEGEND_UNAVAILABLE'
          )}
        </div>
      </div>
      <div className={classes.scrollArea} ref={scrollRef}>
        <div className={classes.track} style={{height: trackHeight}}>
          {hourMarks.map(minute => {
            const top = toTrackTop(minute);
            return (
              <React.Fragment key={minute}>
                <span className={classes.hourLabel} style={{top}}>
                  {minutesToLabel(minute)}
                </span>
                <div className={classes.hourLine} style={{top}} />
              </React.Fragment>
            );
          })}
          {noonOffset !== null && (
            <div className={classes.noonMarker} style={{top: noonOffset}} />
          )}
          {segments.map(segment => {
            const top = toTrackTop(segment.startMinutes);
            const height = Math.max(
              (segment.endMinutes - segment.startMinutes) * PIXELS_PER_MINUTE,
              24
            );
            const isSelected =
              segment.kind === 'available' &&
              selectedSlot?.start_time === segment.slot?.start_time &&
              selectedSlot?.end_time === segment.slot?.end_time;
            const label = `${segment.startLabel} - ${segment.endLabel}`;
            const className = [
              classes.segment,
              segment.kind === 'available' ? classes.available : '',
              segment.kind === 'busy' ? classes.busy : '',
              segment.kind === 'unavailable' ? classes.unavailable : '',
              isSelected ? classes.availableSelected : ''
            ]
              .filter(Boolean)
              .join(' ');

            if (segment.kind === 'available' && segment.slot) {
              return (
                <button
                  key={`available-${segment.startMinutes}-${segment.endMinutes}`}
                  type="button"
                  className={className}
                  style={{top, height}}
                  onClick={() => onSelectSlot(segment.slot as ITimelineSlotItem)}
                  title={label}>
                  {label}
                </button>
              );
            }

            return (
              <div
                key={`${segment.kind}-${segment.startMinutes}-${segment.endMinutes}`}
                className={className}
                style={{top, height}}
                title={
                  segment.kind === 'busy'
                    ? t(
                        'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.BUSY_SLOT_LABEL',
                        {time: label}
                      )
                    : label
                }>
                {segment.kind === 'busy'
                  ? t(
                      'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.BUSY_SHORT'
                    )
                  : ''}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DaySlotTimeline;
